import React, { useState } from 'react';
import { Star, Send, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Quick customer review capture (stored as pending for admin approval).
export const ReviewForm = ({ subject = '', testid = 'review-form' }) => {
  const [f, setF] = useState({ name: '', location: '', email: '', rating: 5, review: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.name.trim() || !f.review.trim()) { toast.error('Please add your name and a short review.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/reviews`, { ...f, subject });
      setDone(true);
      toast.success('Thank you! Your review has been submitted.');
    } catch (e) {
      toast.error('Could not submit your review. Please try again.');
    } finally { setSubmitting(false); }
  };

  return (
    <section className="py-16 section-padding bg-white border-t border-slate-100" data-testid={testid}>
      <div className="max-w-2xl mx-auto bg-[#F1F5F9] rounded-2xl p-7 sm:p-8 border border-slate-200">
        <span className="text-[#B87333] font-semibold text-xs uppercase tracking-[0.2em]">Share your experience</span>
        <h2 className="text-2xl sm:text-3xl font-bold text-[#003456] mt-3">Leave a review</h2>
        {done ? (
          <div className="mt-6 flex items-start gap-3 bg-white border border-[#CE8946]/40 rounded-xl p-5" data-testid={`${testid}-success`}>
            <CheckCircle2 className="h-6 w-6 text-[#CE8946] flex-shrink-0" />
            <p className="text-slate-700 text-sm">Thanks for taking the time — we really appreciate it. Our team reviews submissions before they appear on the site.</p>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            <div className="flex items-center gap-1" data-testid={`${testid}-stars`}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => set('rating', n)} data-testid={`${testid}-star-${n}`} aria-label={`${n} stars`}>
                  <Star className={`h-7 w-7 ${n <= f.rating ? 'text-[#CE8946] fill-[#CE8946]' : 'text-slate-300'}`} />
                </button>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="Your name *" className="px-4 py-2.5 rounded-md border bg-white border-slate-300 text-[#003456] text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid={`${testid}-name`} />
              <input value={f.location} onChange={(e) => set('location', e.target.value)} placeholder="City / region (optional)" className="px-4 py-2.5 rounded-md border bg-white border-slate-300 text-[#003456] text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid={`${testid}-location`} />
            </div>
            <input value={f.email} onChange={(e) => set('email', e.target.value)} placeholder="Email (optional, not published)" className="w-full px-4 py-2.5 rounded-md border bg-white border-slate-300 text-[#003456] text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946]" data-testid={`${testid}-email`} />
            <textarea value={f.review} onChange={(e) => set('review', e.target.value)} rows={4} placeholder="How did your rebuild go? *" className="w-full px-4 py-2.5 rounded-md border bg-white border-slate-300 text-[#003456] text-sm focus:outline-none focus:ring-2 focus:ring-[#CE8946] resize-none" data-testid={`${testid}-text`} />
            <button onClick={submit} disabled={submitting} className="inline-flex items-center justify-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-7 py-3 rounded-md transition-colors disabled:opacity-60" data-testid={`${testid}-submit`}>
              {submitting ? 'Submitting…' : (<>Submit review <Send className="h-4 w-4" /></>)}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default ReviewForm;
